






























































































import { mapGetters, mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import formatDate from '@/helpers/formatDate';
import { formatTimeDuration } from '@/helpers/timeDurationHelper';

import {
  TIMEZONES,
  aiStreamerJobStatusMap,
  CAN_STOP_JOB_STATUSES,
  CAN_FORCE_STOP_JOB_STATUSES
} from '@/views/CopyStake/CopyStakeAiStreamer/types';
import {
  getAiStreamerJobs,
  getAiStreamerJobsStats,
  stopAiStreamerJob
} from '@/api/CopyStake/AiStreamer';
import {
  EAiStreamerJobStatus,
  IAiStreamerJobDetailsRecord
} from '@/api/schema';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { Component } from 'vue';

const DEFAULT_PAGINATION_OPTIONS = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['status'],
  sortDesc: [true]
};

type TJobManagementState = {
  CAN_STOP_JOB_STATUSES: EAiStreamerJobStatus[];
  CAN_FORCE_STOP_JOB_STATUSES: EAiStreamerJobStatus[];
  statusMap: Record<EAiStreamerJobStatus, { label: string; type: string }>;
  statsData: Record<string, number>;

  tableOptions: { page: number; itemsPerPage: number };
  jobsList: IAiStreamerJobDetailsRecord[];
  total: number;

  showConfirmModal: boolean;
  confirmModalPayload: any;

  loading: Record<string, boolean>;
};

const CopyStakeDashboardStatisticTile = (): any =>
  import(
    '@/views/CopyStake/CopyStakeDashboard/CopyStakeDashboardStatisticTile.vue'
  );
const ConfirmationDialog = (): Promise<any> =>
  import('@/components/ConfirmationDialog.vue');
const VStatus = (): Promise<any> => import('@/components/VStatus.vue');
const Tooltip = (): Promise<any> =>
  import('@/components/tooltip/Tooltip.vue');

const TotalJobsIcon = (): any =>
  import('@/assets/images/icons/copyStakeDashboard/total-jobs.svg?inline');
const RunningJobsIcon = (): any =>
  import('@/assets/images/icons/copyStakeDashboard/running-jobs.svg?inline');
const CompletedJobsIcon = (): any =>
  import(
    '@/assets/images/icons/copyStakeDashboard/completed-jobs.svg?inline'
  );
const FailedJobsIcon = (): any =>
  import('@/assets/images/icons/copyStakeDashboard/failed-jobs.svg?inline');
const StoppedJobsIcon = (): any =>
  import('@/assets/images/icons/copyStakeDashboard/stopped-jobs.svg?inline');

export default {
  name: 'CopyStakeJobManagementTab',
  components: {
    CopyStakeDashboardStatisticTile,
    ConfirmationDialog,
    VStatus,
    Tooltip
  },
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data(): TJobManagementState {
    return {
      CAN_STOP_JOB_STATUSES,
      CAN_FORCE_STOP_JOB_STATUSES,
      statusMap: aiStreamerJobStatusMap,
      statsData: undefined,

      tableOptions: { ...DEFAULT_PAGINATION_OPTIONS },
      jobsList: [],
      total: null,

      showConfirmModal: false,
      confirmModalPayload: {},

      loading: {
        stats: false,
        jobsList: false,
        stopJob: false
      }
    };
  },
  watch: {
    active: {
      handler(active: boolean): void {
        if (active) {
          this.fetchDashboardData();
        }
      },
      immediate: false
    },
    operatorId: {
      handler(): void {
        if (this.operatorId !== null) {
          this.fetchAiStreamerJobsStatsData();
        }
      },
      immediate: true
    },
    requestParams: {
      async handler(): Promise<void> {
        await this.fetchAiStreamerJobsData();
      },
      deep: true
    }
  },
  computed: {
    ...mapState('app', ['isSuperAdmin']),
    ...mapGetters('Onboarding', ['operatorId']),
    requestParams(): any {
      return {
        size: this.tableOptions.itemsPerPage,
        page: this.tableOptions.page - 1,
        sort: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
        operatorId: this.operatorId
      };
    },
    timezonesMap(): Record<string, string> {
      return TIMEZONES.reduce((all, curr) => {
        all[curr.value] = curr.text.split(' ')[0];

        return all;
      }, {});
    },
    isLoading(): boolean {
      return Object.values(this.loading).some(Boolean);
    },
    headers(): DataTableHeader[] {
      return [
        { text: 'Job ID', value: 'id' },
        { text: 'Status', value: 'status' },
        { text: 'Start Time', value: 'startTime', width: 240 },
        { text: 'Duration', value: 'sessionDurationSeconds' },
        { text: 'Nickname', value: 'nickname' },
        { text: 'Errors', value: 'errorCount' },
        { text: 'Action', value: 'actions', sortable: false, width: 160 }
      ];
    },

    statisticConfig(): any {
      return [
        {
          title: 'Total Jobs',
          icon: TotalJobsIcon as Component,
          value: this.statsData?.totalJobs
        },
        {
          title: 'Running Jobs',
          icon: RunningJobsIcon as Component,
          value: this.statsData?.runningJobs
        },
        {
          title: 'Completed Jobs',
          icon: CompletedJobsIcon as Component,
          value: this.statsData?.completedJobs
        },
        {
          title: 'Failed Jobs',
          icon: FailedJobsIcon as Component,
          value: this.statsData?.failedJobs
        },
        {
          title: 'Stopped Jobs',
          icon: StoppedJobsIcon as Component,
          value: this.statsData?.stoppedJobs
        }
      ];
    }
  },
  methods: {
    formatTimeDuration,
    convertDate(date: string, zone: string): string {
      return `${formatDate(date, 'yyyy-MM-dd HH:mm', false, false)} ${
        this.timezonesMap[zone]
      }`;
    },
    fetchDashboardData(): void {
      this.fetchAiStreamerJobsData();
      this.fetchAiStreamerJobsStatsData();
    },
    async fetchAiStreamerJobsData(): Promise<void> {
      if (!this.operatorId) return;

      try {
        this.loading.jobsList = true;

        const { content, totalElements } = await getAiStreamerJobs(
          this.requestParams
        );

        this.jobsList =
          content?.map((item: IAiStreamerJobDetailsRecord) => {
            return {
              ...item,
              startTime: this.convertDate(item.startTime, item.zone)
            };
          }) || [];
        this.total = totalElements;
      } catch (error) {
        errorToastMessage(error);
        this.jobsList = [];
      } finally {
        this.loading.jobsList = false;
      }
    },
    async fetchAiStreamerJobsStatsData(): Promise<void> {
      if (!this.operatorId) return;

      try {
        this.loading.stats = true;

        this.statsData = await getAiStreamerJobsStats();
      } catch (error) {
        errorToastMessage(error);
        this.statsData = undefined;
      } finally {
        this.loading.stats = false;
      }
    },
    gracefulStopJob(item: IAiStreamerJobDetailsRecord): void {
      this.loading.stopJob = true;

      stopAiStreamerJob(item.id, false)
        .then(() => {
          this.$toast.success(`Job "${item.id}" was successfully stopped`);
          this.fetchDashboardData();
        })
        .catch((error) => {
          errorToastMessage(error);
        })
        .finally(() => {
          this.loading.stopJob = false;
        });
    },
    forceStopJob(item: IAiStreamerJobDetailsRecord): void {
      this.loading.stopJob = true;

      stopAiStreamerJob(item.id, true)
        .then(() => {
          this.$toast.success(
            `Job "${item.id}" was successfully force stopped`
          );
          this.fetchDashboardData();
          this.showConfirmModal = false;
        })
        .catch((error) => {
          errorToastMessage(error);
        })
        .finally(() => {
          this.loading.stopJob = false;
        });
    },
    handleClickViewJobDetails(item: IAiStreamerJobDetailsRecord): void {
      this.$router.push({
        name: this.isSuperAdmin
          ? 'account-operator-copy-stake-ai-streamers-job-details'
          : 'copy-stake-ai-streamers-job-details',
        params: {
          jobId: item.id.toString()
        }
      });
    },
    handleClickGracefulStopJob(item: IAiStreamerJobDetailsRecord): void {
      this.gracefulStopJob(item);
    },
    handleClickForceStopJob(item: IAiStreamerJobDetailsRecord): void {
      this.confirmModalPayload = {
        header: 'Force Stop AutoClicker Job',
        okText: 'Stop',
        handler: () => this.forceStopJob(item),
        icon: 'mdi-alert-octagon',
        disclaimer: `
          <p>
            Are you sure you want to force stop AutoClicker job <strong>ID ${item.id}</strong>?
          </p>`
      };

      this.showConfirmModal = true;
    }
  }
};
